import styled from '@emotion/styled';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import translateFactory from '../../i18n/translate';
import { font, mediaQueries, palette } from '../css';
import Credits from '../Next/ProgramCredits/ProgramCredits';
import ProgramSuggestion from '../Next/ProgramSuggestion/ProgramSuggestion';
import { getCredits } from '../Pages/Utils';
import { dataToTeaserFromType } from '../Pages/zones/transformers';
import LandscapeSlider from '../Sliders/LandscapeSlider/LandscapeSlider';
import DefaultIcon from '../UI/Icon/Icon';
import RichText from '../UI/RichText/RichText';
import TitlesElement from '../UI/TitlesElement/TitlesElement';
import VideoEmbed from '../UI/VideoEmbed/VideoEmbed';
import VideoThumbnail from '../UI/VideoThumbnail/VideoThumbnail';
import ContactPresse, { propTypes as contactPropTypes } from './ContactPress';
import IconSvg from '../UI/Icon/IconSvg';

const ProgramInfosIcon = (theme) => ({ icon, title }) => (
    <Icon
        key={icon}
        className="metas-infos__icon"
        title={title}
        name={icon}
        color={theme === 'presse' ? 'black' : 'grey'}
        colorTone={theme !== 'presse' ? 'dark' : null}
        size="custom"
    />
);

ProgramInfosIcon.propTypes = {
    icon: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
};

const ProgramInfosIcons = ({ icons, theme }) =>
    icons.length > 0 && (
        <div className="metas-infos__flex">
            <div className="metas-infos__icons">{icons.map((icon) => ProgramInfosIcon(theme)(icon))}</div>
        </div>
    );

const LabelInfo = styled('div')({
    fontWeight: 'bold',
    fontSize: '1.25rem',
});

const TextInfo = styled('span')({
    fontWeight: 'normal',
});

const Premiere = styled('span')({
    fontWeight: 'bold',
    color: palette('arte'),
});

const ProgramInfos = ({ availability, durationLabel, labels, genre }) => (
    <>
        {genre && (
            <LabelInfo>
                {genre} <TextInfo>{durationLabel}</TextInfo>
            </LabelInfo>
        )}
        {availability !== null && (
            <LabelInfo>
                {labels.availability} <TextInfo>{availability}</TextInfo>
            </LabelInfo>
        )}
    </>
);

const BroadcastProgrammings = ({ programs }) =>
    programs && programs.length > 0 ? (
        <>
            <LabelInfo>TV-Ausstrahlung</LabelInfo>
            {programs.map(({ id, broadcastBeginRounded, premiere, premiereLabel }) => {
                return (
                    <div key={id}>
                        {broadcastBeginRounded}
                        {premiere && <Premiere> {premiereLabel || 'Erstausstrahlung'}</Premiere>}
                    </div>
                );
            })}
        </>
    ) : null;

ProgramInfos.propTypes = {
    availability: PropTypes.string,
    className: PropTypes.string,
    durationLabel: PropTypes.string.isRequired,
    icons: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string.isRequired,
            icon: PropTypes.string.isRequired,
        }),
    ),
    labels: PropTypes.shape({
        availability: PropTypes.string.isRequired,
        nextBroadcast: PropTypes.string.isRequired,
        lastDay: PropTypes.string,
    }).isRequired,
    lastDay: PropTypes.bool,
    nextBroadcast: PropTypes.string,
    theme: PropTypes.string,
};

ProgramInfos.defaultProps = {
    availability: '',
    className: '',
    durationLabel: '',
    icons: [],
    labels: {
        availability: 'Disponible : ',
        nextBroadcast: 'Prochaine diffusion : ',
        lastDay: 'Dernier jour',
    },
    lastDay: false,
    nextBroadcast: '',
    trackingEnabled: false,
};

const Icon = styled(DefaultIcon)({
    marginLeft: '10px',
});

const SliderTitle = styled('h2')({
    fontSize: font.size.m,
    fontWeight: font.weight.bold,
});

const PasswordZone = styled('span')({
    color: palette('arte'),
    fontWeight: font.weight.bold,
});

const translations = {
    teasers: {
        availability: {
            available_on: 'Online verfügbar am %{date}',
            available_on_today: 'Online verfügbar um %{hour}',
            broadcast: 'Live',
            broadcast_on: 'TV-Ausstrahlung am %{date}',
            broadcast_on_today: 'TV-Ausstrahlung um %{hour}',
            formats: {
                date: 'dd.MM.',
                hour: 'H:mm',
            },
            livestream: 'Livestream',
            livestream_on: 'Livestream am %{date} um %{hour}',
            livestream_on_today: 'Livestream um %{hour}',
            live_on: 'Live am %{date} um %{hour}',
            live_on_today: 'Live um %{hour}',
        },
        watch: 'Abspielen',
    },
};

let translate = translateFactory('de', translations);

const buildZoneDataAndTitleCollector = () => {
    const dataTransformer = dataToTeaserFromType(translate);

    return (zones) =>
        zones.reduce((agg, zone) => {
            if (zone.data.length > 0) {
                // eslint-disable-next-line
                agg[zone.code.name] = {
                    data: zone.data.map((d) => {
                        const { broadcastAt, beginsAt } = d;

                        return {
                            ...dataTransformer(d),
                            broadcastAt,
                            beginsAt,
                        };
                    }),
                    title: zone.title || '',
                    link: zone.link,
                };
            }

            return agg;
        }, {});
};

const BreadcrumbButton = styled('button')({
    ':hover': {
        cursor: 'pointer',
        color: palette('grey', 'dark'),
    },
});

const BreadcrumbCta = ({ label, link, onClick }) => {
    if (onClick) {
        return (
            <div>
                <BreadcrumbButton onClick={onClick}>{label}</BreadcrumbButton>
            </div>
        );
    }

    if (link) {
        return (
            <div>
                <a href={link}>{label}</a>
            </div>
        );
    }

    return null;
};

BreadcrumbCta.propTypes = {
    label: PropTypes.string,
    link: PropTypes.string,
    onClick: PropTypes.func,
};

// eslint-disable-next-line react/prop-types
export const Player = ({ html, onPlayerEvent }) => {
    const video = <VideoEmbed iframe={html} onPlayerEvent={onPlayerEvent} fullWidth />;

    return <VideoThumbnail video={video} />;
};

const CenteredSection = styled('section')({
    marginLeft: 'auto',
    marginRight: 'auto',
});

const Breadcrumb = styled('nav')({
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '1.25rem',
});

const MetaSection = styled('section')({
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    [mediaQueries.large]: {
        flexDirection: 'row',
    },
});

export const ProgramPage = ({
    breadcrumb,
    contact,
    page,
    password,
    program,
    programMetas,
    onPlayerEvent,
    playerElement,
}) => {
    const { previous, next } = breadcrumb;
    const zoneCollector = buildZoneDataAndTitleCollector();

    const { program_recommendations: asideZone, program_categoryMostViewed: categoryMostViewedZone } = zoneCollector(
        page.zones,
    );

    const titlePart = (
        <React.Fragment>
            <section className="program-section margin-bottom-s">
                <h1>
                    <span className="program-title presse">{program.title}</span>
                    {program.subtitle && <span className="program-title small presse">{program.subtitle}</span>}
                </h1>
            </section>
        </React.Fragment>
    );
    const playerPart = playerElement || <Player html={program.player.html} onPlayerEvent={onPlayerEvent} />;

    const renderTeaserTitle = (title, subtitle) => {
        return <TitlesElement theme="presse" format="twolines" subtitle={subtitle} title={title} />;
    };

    const { hasVideoIsLoggedOut = false, accountLoginURL = '' } = programMetas;
    const displayIconWhenUserDisConnected = hasVideoIsLoggedOut && accountLoginURL;

    const teasers =
        categoryMostViewedZone && categoryMostViewedZone.data
            ? categoryMostViewedZone.data.map((teaser) => ({
                  ...teaser,
                  titlesElement: renderTeaserTitle(teaser.title, teaser.subtitle),
                  iconKind: 'PROGRAM_PRESSE',
                  kind: 'PROGRAM_PRESSE',
                  isIconLeft: true,
              }))
            : [];

    return (
        <div id="program-page">
            <main role="main" id="next_main" tabIndex="-1">
                <div className="row padding-bottom-s padding-top-s">
                    <Breadcrumb className="program-section">
                        {previous && (
                            <BreadcrumbCta
                                label={`< ${previous.label || 'Vorheriges Programm'}`}
                                onClick={previous.onClick}
                                link={previous.link}
                            />
                        )}
                        {next && (
                            <BreadcrumbCta
                                label={`${next.label || 'Nächstes Programm'} >`}
                                onClick={next.onClick}
                                link={next.link}
                            />
                        )}
                    </Breadcrumb>
                </div>
                <CenteredSection className="row collapse">
                    <div className="column large-8">{titlePart}</div>
                </CenteredSection>

                <CenteredSection className="row collapse">
                    <div className={`column large-${asideZone ? '8' : '12'} `}>
                        <section className="program-section margin-bottom-s full-width-xs">
                            {displayIconWhenUserDisConnected && (
                                <div className="program-section__play next-teaser__play-icon">
                                    <a href={accountLoginURL}>
                                        <IconSvg name="arte-icon--play-outline" size="xxlarge" color="white" />
                                    </a>
                                </div>
                            )}

                            {playerPart}
                        </section>

                        <MetaSection className="program-section margin-bottom-s ">
                            <div className={classNames('metas-infos', 'presse')}>
                                <ProgramInfos
                                    theme="presse"
                                    {...programMetas}
                                    genre={program.genrePresse}
                                    labels={{
                                        ...programMetas.labels,
                                        lastDay: programMetas.lastDay ? programMetas.lastDay.label : undefined,
                                    }}
                                />
                                <BroadcastProgrammings programs={program.broadcastProgrammings} />
                                <ProgramInfosIcons icons={programMetas.icons} theme="presse" />
                            </div>
                            <div>
                                <ContactPresse {...contact} />
                                {password && (
                                    <div>
                                        <PasswordZone>Passwort:</PasswordZone> {password}
                                    </div>
                                )}
                            </div>
                        </MetaSection>
                        <section className="program-section margin-bottom-s">
                            <RichText className="presse bold" html={program.shortDescription} />
                        </section>
                        <div>
                            <div>
                                {program.fullDescription && (
                                    <section className="program-section margin-bottom-s">
                                        <RichText className="presse" html={program.fullDescription} />
                                    </section>
                                )}
                                <section className="program-section margin-bottom-s">
                                    <br />
                                    <Credits className="presse" {...getCredits(program)} />
                                </section>
                            </div>
                        </div>
                    </div>
                    {asideZone && (
                        <div className="columns medium-6 large-4 show-for-large aside-desktop">
                            <section className="program-section margin-bottom-s no-margin-left border-top">
                                <ProgramSuggestion
                                    theme="presse"
                                    title={asideZone.title}
                                    titleLevel={2}
                                    teasers={asideZone.data}
                                />
                            </section>
                        </div>
                    )}
                </CenteredSection>
            </main>
            {teasers.length > 0 && (
                <div className="row collapse">
                    <div className="column small-12">
                        <section className="next-section presse border-top">
                            <SliderTitle>{categoryMostViewedZone.title}</SliderTitle>
                            <Icon size="xsmall" color="black" name="arte-download-round" />
                        </section>
                        <section className="margin-bottom-s is-slider category-most-viewed">
                            <LandscapeSlider theme="presse" themeName="presse" teasers={teasers} />
                        </section>
                    </div>
                </div>
            )}
        </div>
    );
};

const breadcrumbElementShape = PropTypes.shape({
    label: PropTypes.string,
    link: PropTypes.string,
    onClick: PropTypes.func,
});
const breadcrumbShape = PropTypes.shape({ next: breadcrumbElementShape, previous: breadcrumbElementShape });

ProgramPage.propTypes = {
    breadcrumb: breadcrumbShape,
    contact: PropTypes.shape({ ...contactPropTypes }),
    password: PropTypes.string,
    page: PropTypes.shape({
        zones: PropTypes.array,
    }).isRequired,
    program: PropTypes.shape({
        id: PropTypes.string,
        player: PropTypes.shape({
            html: PropTypes.string,
        }),
        shortDescription: PropTypes.string,
        fullDescription: PropTypes.string,
        subtitle: PropTypes.string,
        title: PropTypes.string,
    }).isRequired,
    programMetas: PropTypes.shape({
        labels: PropTypes.shape({
            availability: PropTypes.string.isRequired,
            nextBroadcast: PropTypes.string.isRequired,
            lastDay: PropTypes.string,
        }),
        hasVideoIsLoggedOut: PropTypes.bool,
        accountLoginURL: PropTypes.string,
    }).isRequired,
    onPlayerEvent: PropTypes.func,
    playerElement: PropTypes.element,
    imageClassName: PropTypes.string,
};

ProgramPage.defaultProps = {
    onPlayerEvent: () => {},
    password: null,
    playerElement: null,
};

export default ProgramPage;
